import React, { useState } from "react";

interface HoistOption {
  id: string;
  title: string;
  description: string;
  selected: boolean;
}

interface HoistSelectionWizardProps {
  onNext: (selectedHoists: HoistOption[], useTuggers: boolean) => void;
  onBack: () => void;
}

export const HoistSelectionWizard: React.FC<HoistSelectionWizardProps> = ({
  onNext,
  onBack,
}) => {
  const [useTuggers, setUseTuggers] = useState(false);
  const [hoists, setHoists] = useState<HoistOption[]>([
    {
      id: "main",
      title: "Main hoist",
      description: "Primary hoist for heavy loads",
      selected: true,
    },
    {
      id: "auxiliary",
      title: "Auxiliary hoist",
      description: "Transfer of people, with stricter limits",
      selected: false,
    },
    {
      id: "tugger1",
      title: "Load tugger 1",
      description: "Side winch to control load",
      selected: false,
    },
    {
      id: "tugger2",
      title: "Load tugger 2",
      description: "Additional winch to steady load",
      selected: false,
    },
  ]);

  const handleHoistToggle = (hoistId: string) => {
    setHoists((prev) =>
      prev.map((hoist) =>
        hoist.id === hoistId ? { ...hoist, selected: !hoist.selected } : hoist
      )
    );
  };

  const handleNext = () => {
    onNext(hoists, useTuggers);
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center space-x-4 mb-8">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-selection-green rounded-full flex items-center justify-center">
            <span className="text-crane-white text-sm font-medium">✓</span>
          </div>
          <span className="crane-text-body-lg text-crane-white font-medium">
            OPERATION
          </span>
        </div>

        <div className="flex-1 h-px bg-crane-selection-green"></div>

        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-selection-blue rounded-full flex items-center justify-center">
            <span className="text-crane-white text-sm font-medium">2</span>
          </div>
          <span className="crane-text-body-lg text-crane-white font-medium">
            HOISTS
          </span>
        </div>

        <div className="flex-1 h-px bg-crane-darker"></div>

        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-darker rounded-full flex items-center justify-center">
            <span className="text-crane-gray text-sm font-medium">3</span>
          </div>
          <span className="crane-text-body-lg text-crane-gray">PARAMETERS</span>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="crane-text-body-lg text-crane-gray">
        Offshore lift → Lifting operation
      </div>

      {/* Question and Description */}
      <div className="space-y-4">
        <h2 className="crane-text-heading-lg">
          Which hoist will you use for this lift?
        </h2>
        <p className="crane-text-body-lg text-crane-gray">
          Select the hoist or winches you will use for this operation
        </p>
      </div>

      {/* Tugger Toggle */}
      <div className="flex items-center space-x-3">
        <span className="crane-text-body-lg text-crane-white">
          Will you use tuggers to control the load?
        </span>
        <button
          onClick={() => {
            const newUseTuggers = !useTuggers;
            setUseTuggers(newUseTuggers);

            // If disabling tuggers, deselect them
            if (!newUseTuggers) {
              setHoists((prev) =>
                prev.map((hoist) =>
                  hoist.id.includes("tugger")
                    ? { ...hoist, selected: false }
                    : hoist
                )
              );
            }
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            useTuggers ? "bg-crane-selection-green" : "bg-crane-darker"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              useTuggers ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* Hoist Options Grid */}
      <div
        className="grid grid-cols-4 gap-4 w-full min-h-0"
        // style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)" }}
      >
        {hoists
          .filter((hoist) => {
            // Show tuggers only if useTuggers is true
            if (hoist.id.includes("tugger")) {
              return useTuggers;
            }
            return true; // Always show main and auxiliary hoists
          })
          .map((hoist) => (
            <div
              key={hoist.id}
              className={`relative p-6 rounded-lg border-2 cursor-pointer transition-colors min-h-[180px] flex flex-col ${
                hoist.selected
                  ? "border-crane-selection-blue bg-crane-darker"
                  : "border-crane-darker bg-crane-surface hover:border-crane-slate"
              }`}
              onClick={() => handleHoistToggle(hoist.id)}
            >
              {/* Icon Placeholder */}
              <div className="w-12 h-12 bg-crane-light-gray rounded-full mb-4 mx-auto"></div>

              {/* Title and Description */}
              <div className="text-center space-y-2 flex-1">
                <h4 className="crane-text-body-lg font-medium text-crane-white">
                  {hoist.title}
                </h4>
                <p className="crane-text-body-md text-crane-gray">
                  {hoist.description}
                </p>
              </div>

              {/* Selection Indicator */}
              <div className="flex justify-center mt-4">
                <div
                  className={`w-4 h-4 rounded border-2 ${
                    hoist.selected
                      ? "border-crane-selection-green bg-crane-selection-green"
                      : "border-crane-gray"
                  }`}
                >
                  {hoist.selected && (
                    <div className="w-2 h-2 bg-crane-white rounded-sm m-0.5"></div>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 pt-6">
        <button
          onClick={onBack}
          className="crane-button-secondary flex items-center space-x-2"
        >
          <span>←</span>
          <span>Back</span>
        </button>
        <button
          onClick={handleNext}
          className="crane-button-secondary flex items-center space-x-2"
        >
          <span>Next</span>
          <span>→</span>
        </button>
      </div>
    </div>
  );
};
